/**
 * IndustryResearchAgeBadge — surfaces the age of a case's industry research
 * with a one-click refresh CTA when it gets stale.
 *
 * Mounted at the top of the Design wizard (and anywhere else "Industry
 * research is N days old — refresh?" needs to be visible) so consultants
 * notice when the Industry Research backing the case has drifted from the
 * current market context.
 *
 * Behaviour:
 *   - age < `freshThresholdDays` (default 30)  → fresh green badge, "Up to date"
 *   - age < `staleThresholdDays` (default 90)  → amber badge, "N days old · refresh?"
 *   - age >= `staleThresholdDays`              → red badge, "N days old · refresh"
 *
 * Returns null when:
 *   - industryId is missing (case has no linked industry yet)
 *   - the API hasn't returned data yet (silent — don't flicker a badge)
 *   - `lastDataRefreshAt` is absent (industry is a stub with no research yet)
 *
 * Click → navigates to the industry detail page where the user can trigger
 * a refresh from the existing "Regenerate research" controls.
 */
import { Clock, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getIndustry } from '../../lib/api/industryResearchApi';
import { getDataAge } from '../../types/industryResearch';

interface IndustryResearchAgeBadgeProps {
  /** Linked industry's ID from the case context.  Null when no industry attached. */
  industryId: string | null | undefined;
  /** Below this, badge shows "Up to date" (green).  Default 30 days. */
  freshThresholdDays?: number;
  /** At or above this, badge shows red/critical.  Default 90 days. */
  staleThresholdDays?: number;
  /** Override the click handler.  Default opens the industry detail page. */
  onClick?: () => void;
}

export default function IndustryResearchAgeBadge({
  industryId,
  freshThresholdDays = 30,
  staleThresholdDays = 90,
  onClick,
}: IndustryResearchAgeBadgeProps) {
  const navigate = useNavigate();

  // Fetch via getIndustry directly (no dedicated useIndustry hook exists yet).
  // 5-minute staleTime — research age changes daily; no need to refetch often.
  const { data: industry } = useQuery({
    // Share the cache entry with IndustryResearchPanel and IndustryDetail
    // (both use ['industry', industryId] and getIndustry(..., include_research=true)),
    // so the industry isn't fetched twice under two different keys.
    queryKey: ['industry', industryId],
    queryFn: () => getIndustry(industryId as string, true),
    enabled: !!industryId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  if (!industryId) return null;

  // Industry research data carries the timestamp.  Various endpoints return
  // either `industry.lastDataRefreshAt` (preferred) or a nested form via the
  // current version's createdAt — try the canonical field first.
  const lastRefreshAt =
    (industry as any)?.lastDataRefreshAt ||
    (industry as any)?.currentVersion?.createdAt ||
    (industry as any)?.industry?.lastDataRefreshAt;
  const ageDays = getDataAge(lastRefreshAt);

  // No data yet — silent.  Don't blink an empty badge on every Design step.
  if (ageDays === null) return null;

  // Pick palette + copy by age bucket
  let tone: 'fresh' | 'warn' | 'critical';
  if (ageDays < freshThresholdDays) tone = 'fresh';
  else if (ageDays < staleThresholdDays) tone = 'warn';
  else tone = 'critical';

  const palette = {
    fresh: 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100',
    warn: 'text-amber-700 bg-amber-50 border-amber-300 hover:bg-amber-100',
    critical: 'text-red-700 bg-red-50 border-red-300 hover:bg-red-100',
  }[tone];

  const headline =
    tone === 'fresh'
      ? `Industry research is up to date (${ageDays} ${ageDays === 1 ? 'day' : 'days'} old)`
      : `Industry research is ${ageDays} ${ageDays === 1 ? 'day' : 'days'} old — refresh?`;

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    navigate(`/industries/${industryId}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${palette}`}
      title="Open Industry Research to regenerate or review the latest data"
    >
      {tone === 'fresh' ? (
        <Clock className="w-3.5 h-3.5" />
      ) : (
        <RefreshCw className="w-3.5 h-3.5" />
      )}
      <span>{headline}</span>
    </button>
  );
}
